import { Component, Inject, OnInit } from "@angular/core";
import { FormBuilder, FormControl, Validators, FormGroup } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { AlertService } from "src/app/services/alert.service";
import { FarmerService } from "src/app/services/farmer.service";
import { Router } from "@angular/router";
import { EventType } from "src/enum/eventtype";
import { AttachmentType } from "src/enum/attachmentType.enum";
import { environment } from "src/environments/environment";
import * as moment from "moment";

// AE/JE dealer-premises verification — a physical-confirmation-only step (checklist + geo + one
// inspection photo). Deliberately does NOT re-collect engine/chassis/serial/registration/laser-cut —
// all of that is already captured by the dealer at ProductPlacementAtDealerPremises. Modelled on the
// farmer-field inspection popup (AeefarmerpopupComponent), stripped down to just what this step needs.
@Component({
  selector: "app-dealerpremisesverificationpopup",
  templateUrl: "./dealerpremisesverificationpopup.component.html",
  styleUrls: ["./dealerpremisesverificationpopup.component.scss"],
})
export class DealerpremisesverificationpopupComponent implements OnInit {
  form: FormGroup;
  id: any;
  bookedTs: any;
  today: Date;
  maxDate: Date;
  minDate: Date;
  isDisabled: boolean = false;
  loading: boolean = false;
  public eventEnum = EventType;
  verificationList: any = [];
  verificationFormArray: any[] = [];
  attachmentData: any[] = [];
  fileType: number;
  filesResult: any;
  selectedFileName: any;
  telematicsKitFileType: number;
  telematicsKitFilesResult: any;
  selectedTelematicsKitFileName: any;
  physicalVerifyName: any;
  priorVerificationImagePath: string[];
  priorTelematicsKitImagePath: string[];
  environmentVerificationImage = environment.imageBaseUrl + "DBT/DealerPremisesVerificationImage/";
  environmentTelematicsKitImage = environment.imageBaseUrl + "DBT/TelematicsKitImage/";

  constructor(
    private formBuilder: FormBuilder,
    private alertService: AlertService,
    private farmerService: FarmerService,
    private router: Router,
    public dialogRef: MatDialogRef<DealerpremisesverificationpopupComponent>,
    @Inject(MAT_DIALOG_DATA) public dataRecievedData: any
  ) {
    this.id = this.dataRecievedData.id;
    this.bookedTs = this.dataRecievedData.bookedTs;
  }

  ngOnInit(): void {
    this.initializeValidators();
    this.today = new Date();
    this.maxDate = new Date();
    this.minDate = new Date(moment(this.bookedTs).format("YYYY, MM, DD"));
    this.farmerService.getVerification(true).subscribe((res) => {
      this.verificationList = res;
      const priorChecklist = this.dataRecievedData.session?.dealerPremisesVerificationList;
      if (priorChecklist && priorChecklist.length > 0) {
        this.verificationList.forEach((element) => {
          const matched = priorChecklist.filter((x) => x.key == element.key);
          if (matched && matched.length > 0) {
            element.checked = true;
            this.verificationFormArray.push(element.key);
          }
        });
      }
    });
    if (this.dataRecievedData.session?.dealerPremisesVerificationTs) {
      this.form.controls["verificationTs"].setValue(
        this.dataRecievedData.session.dealerPremisesVerificationTs
      );
    }
    this.priorVerificationImagePath = this.dataRecievedData.session?.dealerPremisesVerificationImagePath;
    this.priorTelematicsKitImagePath = this.dataRecievedData.session?.telematicsKitImagePath;
  }

  initializeValidators() {
    this.form = this.formBuilder.group({
      verificationTs: [null, Validators.required],
      productImage_file: [null, Validators.required],
      telematicsKitImage_file: [null],
      physicalVerifyReport: [null, Validators.required],
      remarks: [null, Validators.required],
      latitude: [null],
      longitude: [null],
    });
    this.applyTelematicsKitValidator();
  }

  // Telematics Kit Image is mandatory only when the machinery's price (session.totalCost) is
  // above ₹1,00,000 — same threshold/field as the laser-cut requirement in aeefarmerpopup.component.ts.

  get isTelematicsKitMandatory(): boolean {
    if (this.dataRecievedData?.session?.manufacturerType != 1) {
      return false;
    }
    return (this.dataRecievedData?.session?.totalCost ?? 0) > 100000;
  }

  applyTelematicsKitValidator() {
    if (this.isTelematicsKitMandatory) {
      this.form.controls["telematicsKitImage_file"].setValidators(Validators.required);
    } else {
      this.form.controls["telematicsKitImage_file"].clearValidators();
    }
    this.form.controls["telematicsKitImage_file"].updateValueAndValidity();
  }

  next() {
    this.dialogRef.close();
    this.router.navigate(["/aefarmerbookingview"]);
  }

  validateFormControl() {
    Object.keys(this.form.controls).forEach((field) => {
      const control = this.form.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({
          onlySelf: true,
        });
      }
    });
  }

  clear(event) {
    event.target.value = null;
  }

  onFileSelected(event1) {
    const file = event1.target.files[0];
    if (
      file &&
      (file.type === "image/png" ||
        file.type === "image/jpg" ||
        file.type === "image/jpeg")
    ) {
      let filesize = file.size;
      let acceptsize = parseInt((filesize / 1024).toFixed(2));
      if (acceptsize < 2048) {
        this.selectedFileName = file.name;
        this.fileType = 1;
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event: any) => {
          this.filesResult = event.target.result.split(",")[1];
          if (this.filesResult) {
            this.form.controls["productImage_file"].setValue(this.selectedFileName);
          }
          this.attachmentData.push({
            id: 0,
            attachmentFilename: this.selectedFileName,
            base64String: this.filesResult,
            attachmentType: AttachmentType.DealerPremisesVerificationImage,
            statusType: 2,
            fileType: this.fileType,
          });
        };
      } else {
        this.alertService.error("File size is large, Please upload less than 2 MB");
        this.selectedFileName = "";
      }
    } else {
      this.alertService.error("Please upload JPG & PNG images");
    }
  }

  onTelematicsKitFileSelected(event1) {
    const file = event1.target.files[0];
    if (
      file &&
      (file.type === "image/png" ||
        file.type === "image/jpg" ||
        file.type === "image/jpeg")
    ) {
      let filesize = file.size;
      let acceptsize = parseInt((filesize / 1024).toFixed(2));
      if (acceptsize < 2048) {
        this.selectedTelematicsKitFileName = file.name;
        this.telematicsKitFileType = 1;
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event: any) => {
          this.telematicsKitFilesResult = event.target.result.split(",")[1];
          if (this.telematicsKitFilesResult) {
            this.form.controls["telematicsKitImage_file"].setValue(this.selectedTelematicsKitFileName);
          }
          this.attachmentData.push({
            id: 0,
            attachmentFilename: this.selectedTelematicsKitFileName,
            base64String: this.telematicsKitFilesResult,
            attachmentType: AttachmentType.TelematicsKitImage,
            statusType: 2,
            fileType: this.telematicsKitFileType,
          });
        };
      } else {
        this.alertService.error("File size is large, Please upload less than 2 MB");
        this.selectedTelematicsKitFileName = "";
      }
    } else {
      this.alertService.error("Please upload JPG & PNG images");
    }
  }

  downloadattachament() {
    this.farmerService.getInspectionReport(this.id, 2, true).subscribe((res) => {
      if (res) {
        const linkSource = "data:application/pdf;base64, " + res;
        const downloadLink = document.createElement("a");
        const fileName = "Physical Verification.pdf";
        downloadLink.href = linkSource;
        downloadLink.download = fileName;
        downloadLink.click();
      }
    });
  }

  PhysicalVerificationUpload(event) {
    const file = event.target.files[0];
    if (
      file.type === "application/pdf" ||
      file.type === "image/png" ||
      file.type === "image/jpg" ||
      file.type === "image/jpeg"
    ) {
      let filesize = file.size;
      let acceptsize = parseInt((filesize / 1024).toFixed(2));
      this.physicalVerifyName = file.name;
      let checkType = file.type;
      let getFileType = checkType.split("/");
      if (getFileType[1] == "pdf") {
        this.fileType = 2;
      } else {
        this.fileType = 1;
      }
      if (acceptsize < 2048) {
        const reader = new FileReader();
        reader.readAsDataURL(event.target.files[0]);
        reader.onload = (event: any) => {
          this.filesResult = event.target.result.split(",")[1];
          this.form.controls["physicalVerifyReport"].setValue(this.filesResult);
          this.attachmentData.push({
            id: 0,
            attachmentFilename: file.name,
            attachmentType: 52,
            fileType: this.fileType,
            base64String: this.filesResult,
          });
        };
      } else {
        this.alertService.error("File size is large, Please upload less than 2 MB");
        this.physicalVerifyName = "";
      }
    } else {
      this.alertService.error("Please upload JPG, JPEG, PNG and PDF only");
    }
  }

  onChange(key: number, event: any) {
    if (event.checked) {
      this.verificationFormArray.push(key);
    } else {
      let index = this.verificationFormArray.indexOf(key);
      this.verificationFormArray.splice(index, 1);
    }
  }

  onSave() {
    this.applyTelematicsKitValidator();
    if (this.form.valid) {
      this.isDisabled = true;
      this.loading = true;
      let request = {
        id: this.id,
        acNo: "",
        cityId: null,
        branchId: null,
        ifsc: "",
        dbtSchemeId: null,
        dbtComponentId: null,
        dbtProductId: null,
        dbtProductInfoId: null,
        dbtManufacturerId: null,
        dbtDealerId: null,
        eventType: this.eventEnum.DealerPremisesVerification,
        bankId: 0,
        transactionType: 0,
        paymentTs: null,
        receiptNumber: "",
        productCost: 0,
        gstPercentage: 0,
        subsidyAmount: 0,
        totalCost: 0,
        deliveryTs: null,
        dealerPremisesVerificationTs: moment(this.form.value.verificationTs).format("YYYY-MM-DD"),
        verificationIds: this.verificationFormArray,
        remarks: this.form.value.remarks,
        attachment: this.attachmentData,
        latitude: this.form.value.latitude || 0,
        longitude: this.form.value.longitude || 0,
      };
      this.farmerService.farmerSave(request).subscribe((res) => {
        if (res && res.isSuccess) {
          this.alertService.success("Successfully saved the dealer premises verification details.");
          this.dialogRef.close();
          this.router.navigate(["/aefarmerbooking"]);
        }
        this.isDisabled = false;
        this.loading = false;
      });
    } else {
      this.validateFormControl();
    }
  }
}
